import React, {Component, FormEvent, FocusEvent, RefObject, createRef} from 'react';
import {Button, InputAdornment} from '@material-ui/core';
import Form from 'components/forms/Form';
import './index.scss';
import TextField from 'components/forms/TextField';
import {ValidationGroup, Validators} from 'util/validation';
import {NotFoundError, ValueError} from 'util/errors';
import ProgressSpinner from 'components/ProgressSpinner';
import {DraftBooking} from 'services/entities/booking';
import {addDraftBookingDiscount, saveDraftBooking} from 'services/bookings/drafts';
import {elementIsDescendant} from 'util/html';

enum AddDiscountFormStatus {
  Hidden,
  Visible,
  Submitting,
  Success,
  Failure
}

interface AddDiscountFormProps {
  booking: DraftBooking|null;
  onDiscountsChanged?: ()=>void;
}

interface AddDiscountFormState {
  status: AddDiscountFormStatus
}

export default class AddDiscountForm extends Component<AddDiscountFormProps, AddDiscountFormState> {
  private _formRef:RefObject<HTMLFormElement> = createRef();
  private readonly validation: ValidationGroup;
  private readonly model: {code:string};
  state = {
    status: AddDiscountFormStatus.Hidden
  };

  constructor(props: any) {
    super(props);
    this.model = { code: "" };
    this.validation = new ValidationGroup(this.model);
    this.validation.addField("code", {
      required: Validators.required
    });
  }

  onAddClick = () => {
    this.setState({status: AddDiscountFormStatus.Visible});
  };

  onAddDiscount = () => {
    const booking = this.props.booking;
    if(!booking) {
      return;
    }
    this.setState({status: AddDiscountFormStatus.Submitting});
    addDraftBookingDiscount(booking, this.model.code).then(
      () => {
        this.setState({status: AddDiscountFormStatus.Success});
        saveDraftBooking(booking);
        if(this.props.onDiscountsChanged) {
          this.props.onDiscountsChanged();
        }
      },
      ex => {
        if(ex instanceof NotFoundError) {
          this.validation.addFieldError('code', 'notFound');
          this.setState({status: AddDiscountFormStatus.Visible});
        } else if(ex instanceof ValueError) {
          this.validation.addFieldError('code', ex.errors.length > 0 ? ex.errors[0].toString() : 'unknown');
          this.setState({status: AddDiscountFormStatus.Visible});
        } else {
          this.validation.addFieldError('code', 'unknown');
          this.setState({status: AddDiscountFormStatus.Visible});
        }
      }
    );
  };

  onCodeFieldRendered = (el:HTMLInputElement|null) => {
    if(el && this.state.status === AddDiscountFormStatus.Visible) {
      el.focus();
    }
  };

  onFieldBlur = (el:FocusEvent) => {
    if(this.model.code) {
      // don't hide if there is some input
      return;
    } else if(!el.relatedTarget || !this._formRef.current || !elementIsDescendant(el.relatedTarget as HTMLElement, this._formRef.current)) {
      this.setState({status: AddDiscountFormStatus.Hidden});
    }
  };

  onFieldInput = (ev: FormEvent<HTMLInputElement>) => {
    const el = ev.target as HTMLInputElement;
    let value = el.value;
    value = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    this.model.code = value;
    el.value = value;
    this.setState({ status: AddDiscountFormStatus.Visible });
  };

  renderAddButton() {
    return <Button className="add" onClick={this.onAddClick}>Have a promo code? Click here</Button>;
  }

  renderForm() {
    const disabled = this.state.status === AddDiscountFormStatus.Submitting || this.state.status === AddDiscountFormStatus.Success;
    return <Form onSubmit={this.onAddDiscount} validationGroup={this.validation} formRef={this._formRef}>
      <TextField
        name="code"
        type="text"
        className="code"
        required={true}
        validationGroup={this.validation}
        model={this.model}
        validationMessages={{
          required: "Discount code is required",
          notFound: "Unknown discount code",
          maxUsagesExceeded: "You have already used this code",
          unknown: "Failed to add discount code" }}
        disabled={disabled}
        onInput={this.onFieldInput}
        onBlur={this.onFieldBlur}
        inputRef={this.onCodeFieldRendered}
        endAdornment={<InputAdornment position="end">
          { this.state.status !== AddDiscountFormStatus.Submitting
            ? <Button type="submit" disabled={disabled}>Apply</Button>
            : <ProgressSpinner /> }
        </InputAdornment>}/>
    </Form>;
  }

  renderSuccessMessage() {
    return <span className="success">Promo code applied!</span>;
  }

  render() {
    let status = this.state.status;
    if(this.props.booking && this.props.booking!.discounts.length > 0) {
      status = AddDiscountFormStatus.Success;
    }
    return <div className="add-discount-form">
      { status === AddDiscountFormStatus.Hidden && this.renderAddButton() }
      { status !== AddDiscountFormStatus.Hidden && status !== AddDiscountFormStatus.Success && this.renderForm() }
      { status === AddDiscountFormStatus.Success && this.renderSuccessMessage() }
    </div>;
  }
}